import { useContext, useMemo } from "react"
import HomeLayout from "modules/home-layout"
import useContextDashboard from "./context/dashboard"
import PreferenceContext from "hooks/preferences"
import { useAccountId } from "resources/account"
import Loading from "components/loading"
import { AiContext } from "connected-components/ai-teammate/context/ai-context"
import { ModelContext, useModelContext } from "./context/model"
import { useClearDashboardPreferences } from "./hooks/use-clear-dashboard-preferences"
import { DashboardIdProvider } from "./context/dashboard-id"

function DashboardWithContext({ children }) {
  // This needs to be done here it needs to be a child of the PreferenceContext.Provider for the usePreferences hook to work
  const [DashboardContext, dashboardContextValue] = useContextDashboard()
  const { modelId } = useContext(ModelContext)

  const aiContextValue = useMemo(() => ({
    modelId,
    instrument: dashboardContextValue.instrument,
    base: dashboardContextValue.base,
    startDate: dashboardContextValue.startDate,
    endDate: dashboardContextValue.endDate,
    isPreferencesLoaded: dashboardContextValue.isPreferencesLoaded,
    canPinArtifacts: true,
  }), [
    modelId,
    dashboardContextValue.instrument,
    dashboardContextValue.base,
    dashboardContextValue.startDate,
    dashboardContextValue.endDate,
    dashboardContextValue.isPreferencesLoaded,
  ])

  if (!dashboardContextValue.isPreferencesLoaded) {
    return <Loading />
  }

  return (
    <DashboardContext.Provider value={dashboardContextValue}>
      <AiContext.Provider value={aiContextValue}>
        {children}
      </AiContext.Provider>
    </DashboardContext.Provider>
  )
}

function DashboardWithModel({ children }) {
  const [ModelContextComponent, modelContextValue] = useModelContext()

  if (!modelContextValue.isPreferencesLoaded) {
    return <Loading />
  }

  return (
    <ModelContextComponent.Provider value={modelContextValue}>
      {children}
    </ModelContextComponent.Provider>
  )
}

export default function DashboardHomeLayout() {
  const accountId = useAccountId()

  // Check for clearPreferences URL param and handle clearing BEFORE any dashboard queries
  // This must be called before the PreferenceContext.Provider to ensure preferences are cleared before they are loaded
  const { isClearing: isPreferencesClearing } = useClearDashboardPreferences()

  const preferencesProps = useMemo(() => ({
    accountId,
    // Since we can only have one dashboard per account, there is no need to have a more complex key.
    // Each context will append its unique identifier to the key.
    // eg. dashboard.asset-view, dashboard.kpi, dashboard.factors-view
    key: "dashboard",
    type: "dashboard",
  }), [accountId])

  if (isPreferencesClearing) {
    return <Loading />
  }

  return (
    <PreferenceContext.Provider value={preferencesProps}>
      <DashboardIdProvider value="default">
        <DashboardWithModel>
            <DashboardWithContext>
              <HomeLayout />
            </DashboardWithContext>
        </DashboardWithModel>
      </DashboardIdProvider>
    </PreferenceContext.Provider>
  )
}
